import {useEffect, useMemo, useState} from "react";
import {Autocomplete, Box, CircularProgress, TextField} from "@mui/material";
import _ from "lodash";
import {organizationSettingsService} from "_services";
import {Auth} from "components/Authorization";
import {SnackBar} from "components/common";
import ActionButton from "../../../../../common/ActionButton";

const compactAutocompleteListboxProps = {
    sx: {
        py: 0.5,
        "& .MuiAutocomplete-option": {
            minHeight: 30,
            py: 0.25,
            px: 1.25,
            fontSize: "0.8125rem",
            fontFamily: "LarsseitRegular",
            lineHeight: 1.2,
        },
    },
};

function handleApiError(error, fallback) {
    if (_.get(error, 'response.status') === 401) {
        Auth.processLogout();
        return;
    }
    const message = _.get(error, 'response.data.message') || _.get(error, 'message') || fallback;
    SnackBar.showMessage(message);
}

function normalizeIdList(value) {
    if (!Array.isArray(value)) return [];
    return value.map((id) => (id == null ? null : String(id))).filter(Boolean);
}

function toOptionsList(items) {
    if (!Array.isArray(items)) return [];
    return items
        .filter((item) => item && item.id != null)
        .map((item) => ({id: String(item.id), name: item.name || String(item.id)}));
}

export default function GorgiasViewsSetupContent({trendsId, handleClose}) {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [views, setViews] = useState([]);
    const [users, setUsers] = useState([]);
    const [selectedViewIds, setSelectedViewIds] = useState([]);
    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const [tagsList, setTagsList] = useState('');

    useEffect(() => {
        let cancelled = false;
        if (trendsId == null) {
            setLoading(false);
            return undefined;
        }
        setLoading(true);
        organizationSettingsService.getTrendsGorgiasViewsData(trendsId)
            .then((response) => {
                if (cancelled) return;
                const data = response?.data || {};
                setViews(toOptionsList(data.views));
                setUsers(toOptionsList(data.users));
                setSelectedViewIds(normalizeIdList(data.selectedViewIds));
                setSelectedUserIds(normalizeIdList(data.selectedUserIds));
                setTagsList(typeof data.tagsList === 'string' ? data.tagsList : '');
            })
            .catch((error) => {
                if (cancelled) return;
                handleApiError(error, 'Failed to load Gorgias views data.');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [trendsId]);

    const viewsById = useMemo(() => _.keyBy(views, 'id'), [views]);
    const usersById = useMemo(() => _.keyBy(users, 'id'), [users]);

    const selectedViewOptions = selectedViewIds.map((id) => viewsById[id] || {id, name: id});
    const selectedUserOptions = selectedUserIds.map((id) => usersById[id] || {id, name: id});

    const handleSave = () => {
        if (saving || trendsId == null) return;
        setSaving(true);
        const payload = {
            viewIds: selectedViewIds,
            userIds: selectedUserIds,
            tagsList,
        };
        organizationSettingsService.saveTrendsGorgiasViewsData(trendsId, payload)
            .then((response) => {
                const data = response?.data || {};
                setSelectedViewIds(normalizeIdList(data.selectedViewIds || selectedViewIds));
                setSelectedUserIds(normalizeIdList(data.selectedUserIds || selectedUserIds));
                if (typeof data.tagsList === 'string') {
                    setTagsList(data.tagsList);
                }
                SnackBar.showMessage('Gorgias views settings saved.');
                handleClose();
            })
            .catch((error) => {
                handleApiError(error, 'Failed to save Gorgias views settings.');
            })
            .finally(() => {
                setSaving(false);
            });
    };

    if (loading) {
        return (
            <Box sx={styles.loadingContainer}>
                <CircularProgress size={24} sx={styles.spinner}/>
            </Box>
        );
    }

    return (
        <Box>
            <Box sx={styles.note}>
                <b>NOTE:</b> Enabling Gorgias Views will deactivate Webhooks for the account. Also, once
                all Gorgias Views are removed/unselected and submitted, webhook will be reactivated.
                <br/>
                Please ensure all required views are added when enabling as only messages in these views
                will get saved when Gorgias Views is enabled.
            </Box>

            <Box sx={styles.field}>
                <Box sx={styles.label}>
                    Gorgias View List IDs <Box component={'span'} sx={styles.required}>*</Box>
                </Box>
                <Autocomplete
                    multiple
                    size={'small'}
                    options={views}
                    value={selectedViewOptions}
                    onChange={(event, newValue) => {
                        setSelectedViewIds(newValue.map((option) => option.id));
                    }}
                    getOptionLabel={(option) => option?.name || ''}
                    isOptionEqualToValue={(a, b) => a?.id === b?.id}
                    ListboxProps={compactAutocompleteListboxProps}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder={selectedViewIds.length ? '' : 'Select Gorgias Views'}
                        />
                    )}
                />
                <Box sx={styles.helpText}>Please select the views to be saved</Box>
            </Box>

            <Box sx={styles.field}>
                <Box sx={styles.label}>
                    Skip Tags List (Messages containing these tags WILL NOT be saved)
                </Box>
                <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    value={tagsList}
                    onChange={(event) => setTagsList(event.target.value)}
                />
                <Box sx={styles.helpText}>Please enter the tags as a comma separated string</Box>
            </Box>

            <Box sx={styles.field}>
                <Box sx={styles.label}>
                    Approved Gorgias Assignee IDs (Only unassigned Messages or Messages Assigned to
                    these users will be saved)
                </Box>
                <Autocomplete
                    multiple
                    size={'small'}
                    options={users}
                    value={selectedUserOptions}
                    onChange={(event, newValue) => {
                        setSelectedUserIds(newValue.map((option) => option.id));
                    }}
                    getOptionLabel={(option) => option?.name || ''}
                    isOptionEqualToValue={(a, b) => a?.id === b?.id}
                    ListboxProps={compactAutocompleteListboxProps}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder={selectedUserIds.length ? '' : 'Select Gorgias Users'}
                        />
                    )}
                />
                <Box sx={styles.helpText}>Please select the approved users</Box>
            </Box>

            <Box sx={styles.buttonContainer}>
                <Box sx={styles.cancelButtonContainer}>
                    <ActionButton
                        label={'Cancel'}
                        action={handleClose}
                        buttonBordered
                        useBorderColor={'#003300'}
                        withIcon={false}
                        sx={styles.cancelButton}
                        type={'button'}
                        disabled={saving}
                    />
                </Box>
                <Box sx={styles.saveButtonContainer}>
                    <ActionButton
                        label={saving ? 'Saving…' : 'Save'}
                        action={handleSave}
                        withIcon={false}
                        sx={styles.saveButton}
                        type={'button'}
                        disabled={saving}
                    />
                </Box>
            </Box>
        </Box>
    );
}

const styles = {
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        py: '40px',
    },
    spinner: {
        color: '#003300',
    },
    note: {
        backgroundColor: '#F8F9F9',
        border: '1px solid #b6bebf',
        borderRadius: '8px',
        padding: '12px 16px',
        mb: '20px',
        fontFamily: 'LarsseitRegular',
        fontSize: '14px',
        color: '#495D5E',
        lineHeight: 1.4,
    },
    field: {
        mb: '20px',
    },
    label: {
        fontFamily: 'LarsseitMedium',
        fontSize: '14px',
        fontWeight: 500,
        color: '#495D5E',
        mb: '5px',
    },
    required: {
        color: '#AC2925',
        ml: '4px',
    },
    helpText: {
        mt: '6px',
        fontFamily: 'LarsseitRegular',
        fontSize: '13px',
        color: '#6b6b6b',
    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
        paddingTop: '10px',
    },
    cancelButtonContainer: {
        width: '120px',
        marginRight: '15px',
    },
    cancelButton: {
        height: '40px',
    },
    saveButtonContainer: {
        width: '120px',
    },
    saveButton: {
        height: '40px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
};
